<template>
  <div class="tier-step">
    <h2 class="tier-step-title">{{ is_edit ? 'Edit your tiers' : 'Define your tiers' }}</h2>
    <p class="tier-step-desc">
      Tailor your tiers with icons and colors that reflect your brand identity. Include descriptions to help creators
      grasp the eligibility criteria for each tier, which will appear as tooltips. The default tier is set to the lowest
      position.
    </p>
    <p class="tier-step-hint">Rank by highest to lowest</p>

    <Button
      class="tier-add-btn"
      :style="'border'"
      color="black-90"
      size="32"
      :border_r="6"
      :is_disabled="is_full"
      @click="add"
    >
      Add tier
    </Button>

    <drop-list class="tier-list" :items="tiers" @reorder="onReorder">
      <template #item="{ item, index }">
        <drag
          :key="item.key"
          :class="['tier-item', index === tiers.length - 1 && 'is-last']"
          handle=".tier-drag-handle"
          :disabled="isStartingTier(item)"
        >
          <div class="tier-rank">
            <!-- the number is the position from the top, not the API rank -->
            <span :class="['tier-rank-num', index === 0 && 'is-top']">{{ index + 1 }}</span>
          </div>

          <div class="tier-card tier-define-card">
            <i class="tier-drag-handle" v-if="!isStartingTier(item)">
              <svg-icon name="drag" />
            </i>

            <div class="tier-card-body">
              <div class="tier-card-tag" v-if="isStartingTier(item)">(Default tier)</div>

              <div class="tier-fields">
                <div class="tier-icon-field">
                  <span class="tier-field-label">Icon</span>
                  <div class="tier-icon-wrap">
                    <label class="tier-icon-btn">
                      <img v-if="item.icon" :src="item.icon" alt="" />
                      <SvgIcon v-else name="plus" />
                      <input
                        class="tier-icon-input"
                        type="file"
                        :accept="icon_accept"
                        @change="loadIcon($event, item)"
                      />
                    </label>
                    <!-- outside the label, so clicking it does not reopen the file picker -->
                    <i class="tier-icon-clear" v-if="item.icon" title="Remove icon" @click="clearIcon(item)">
                      <SvgIcon name="close" />
                    </i>
                  </div>
                </div>

                <div class="tier-name-field">
                  <label class="tier-field-label">Tier name<span class="tier-required">*</span></label>
                  <el-input
                    :class="[nameError(item) && 'err-input']"
                    v-model="item.name"
                    :maxlength="name_max"
                    placeholder="Tier name"
                  />
                </div>

                <TierColorField label="Font and border color" :default_value="default_font" v-model="item.font_color" />
                <TierColorField
                  label="Background color"
                  :default_value="default_background"
                  v-model="item.background_color"
                />
              </div>

              <p class="tier-name-error" v-if="nameError(item)">Every tier needs a name before continuing.</p>

              <div class="tier-desc-field">
                <label class="tier-field-label">Description</label>
                <el-input
                  v-model="item.description"
                  type="textarea"
                  :rows="3"
                  :maxlength="description_max"
                  show-word-limit
                  resize="none"
                  placeholder="Describe this tier"
                />
              </div>

              <div class="tier-preview" v-if="item.show_preview">
                <div class="tier-preview-item" v-for="preview in preview_sizes" :key="preview.size">
                  <span class="tier-preview-size">{{ preview.size }}</span>
                  <TierBadge :tier="item" :size="preview.size" />
                </div>
              </div>
            </div>

            <div class="tier-actions">
              <tippy content="Preview how this tier looks to creators" @click="item.show_preview = !item.show_preview">
                <i :class="['tier-action', 'ico-preview', item.show_preview && 'is-on']">
                  <SvgIcon :name="item.show_preview ? 'eye-hidden' : 'eye'" />
                </i>
              </tippy>
              <tippy v-if="!isStartingTier(item)" content="Remove this tier" @click="askRemove(index)">
                <i class="tier-action ico-delete"><SvgIcon name="delete2" /></i>
              </tippy>
            </div>
          </div>
        </drag>
      </template>

      <template #feedback><div class="tier-drop-feedback"></div></template>
    </drop-list>

    <TierConfirm
      :is_show="remove_index !== null"
      :title="`Are you sure you want to remove the ${removing_name} tier?`"
      :lines="['Creators will transition to different tiers.']"
      confirm_text="Confirm"
      @cancel="remove_index = null"
      @confirm="confirmRemove"
    />
  </div>
</template>

<script>
import { ElInput } from 'element-plus';
import { Drag, DropList } from 'vue-easy-dnd';
import 'vue-easy-dnd/dist/dnd.css';
import Button from '@/components/ui/Button.vue';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import TierBadge from './TierBadge.vue';
import TierColorField from './TierColorField.vue';
import TierConfirm from './TierConfirm.vue';
import { ICON_ACCEPT, ICON_TOO_LARGE, resizeIcon } from './tierIcon';
import {
  createTier,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_FONT_COLOR,
  DESCRIPTION_MAX,
  isStartingTier,
  MAX_TIERS,
  NAME_MAX,
  PREVIEW_SIZES,
  syncRanks,
} from './tierModel';

export default {
  name: 'TierDefineStep',

  components: { ElInput, Drag, DropList, Button, SvgIcon, TierBadge, TierColorField, TierConfirm },

  props: {
    tiers: {
      type: Array,
      default: () => [],
    },
    is_edit: {
      type: Boolean,
      default: false,
    },
    // flipped on by the dialog when Next hits a missing name
    show_errors: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['notify'],

  data() {
    return {
      default_font: DEFAULT_FONT_COLOR,
      default_background: DEFAULT_BACKGROUND_COLOR,
      description_max: DESCRIPTION_MAX,
      name_max: NAME_MAX,
      icon_accept: ICON_ACCEPT,
      preview_sizes: PREVIEW_SIZES,
      // index of the tier awaiting delete confirmation, null when nothing is pending
      remove_index: null,
    };
  },

  computed: {
    is_full() {
      return this.tiers.length >= MAX_TIERS;
    },

    removing_name() {
      const tier = this.remove_index === null ? null : this.tiers[this.remove_index];
      return tier?.name.trim() || 'this';
    },
  },

  methods: {
    isStartingTier,

    // clears itself as soon as something is typed, no need to re-run validation
    nameError(tier) {
      return this.show_errors && !tier.name.trim();
    },

    add() {
      if (this.is_full) return;
      // a new tier takes the top position, so it is the highest rank and never the default
      // one: only the bottom row carries the Account Creation condition.
      this.tiers.unshift(createTier(false));
      syncRanks(this.tiers);

      // the new card is first, so the first name input in the document is its own
      this.$nextTick(() => {
        this.$el.querySelector('.tier-item .tier-name-field .el-input__inner')?.focus();
      });
    },

    askRemove(index) {
      // every new creator lands on the default tier, so the set can never be left without one
      if (isStartingTier(this.tiers[index])) return;
      this.remove_index = index;
    },

    confirmRemove() {
      const index = this.remove_index;
      this.remove_index = null;
      if (index === null) return;

      this.tiers.splice(index, 1);
      syncRanks(this.tiers);
    },

    onReorder(event) {
      if (!event || !event.apply) return;
      event.apply(this.tiers);
      this.keepStartingLast();
      syncRanks(this.tiers);
    },

    // the default tier owns the lowest position, so it is pushed back if dropped elsewhere
    keepStartingLast() {
      const index = this.tiers.findIndex((tier) => tier.is_starting);
      if (index !== -1 && index !== this.tiers.length - 1) {
        this.tiers.push(this.tiers.splice(index, 1)[0]);
      }
    },

    clearIcon(tier) {
      // only a preview built here is ours to release, a loaded icon is a remote URL
      if (tier.icon.startsWith('blob:')) URL.revokeObjectURL(tier.icon);
      tier.icon = '';
      tier.icon_file = null;
      // a loaded icon lives on the server, so dropping it has to be sent explicitly
      tier.icon_removed = true;
    },

    loadIcon(event, tier) {
      const input = event.target;
      // input.files is live: clearing the value empties it, so hold the File first
      const picked = input.files?.[0];
      input.value = '';
      if (!picked) return;

      resizeIcon(picked)
        .then((file) => {
          if (tier.icon.startsWith('blob:')) URL.revokeObjectURL(tier.icon);
          tier.icon = URL.createObjectURL(file);
          tier.icon_file = file;
          tier.icon_removed = false;
        })
        .catch((err) => {
          console.log(err);
          this.$emit('notify', err.code === ICON_TOO_LARGE ? err.message : 'Could not read that image.');
        });
    },
  },
};
</script>
<style lang="scss">
.tier-step {
  .tier-step-title {
    font: var(--font-med-24);
    color: var(--black-90);
  }

  .tier-step-desc {
    color: var(--black-90);
    margin: 8px 0;
  }

  .tier-step-hint {
    color: var(--black-50);
    margin: 4px 0 16px;
  }

  .tier-field-label {
    display: block;
    font: var(--font-med-14);
    color: var(--black-90);
    margin-bottom: 8px;
  }

  .tier-required {
    color: var(--red);
    margin-left: 2px;
  }

  .tier-reset {
    color: var(--black-75);
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: var(--blue);
    }
  }

  // element-plus draws the input border with an inset box-shadow, not a border
  .err-input {
    .el-input__wrapper,
    .el-textarea__inner {
      box-shadow: 0 0 0 1px var(--alert) inset;
    }
  }

  .el-input__wrapper {
    height: 40px;
    border-radius: 6px;
  }

  .el-textarea__inner {
    border-radius: 6px;
  }
}

.tier-add-btn {
  margin-bottom: 16px;
}

.tier-item {
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: stretch;

  &.is-last {
    // the last card is the default tier: no drag handle, so no room to reserve for it.
    // scoped to the define step's own card, the conditions step must not inherit it.
    .tier-define-card {
      padding-left: 16px;

      .tier-actions .tier-action.ico-preview {
        top: 146px;
      }
    }

    .tier-rank::after {
      display: none;
    }
  }
}

.tier-rank {
  position: relative;
  display: flex;
  justify-content: center;

  &::after {
    content: '';
    position: absolute;
    top: 16px;
    bottom: -16px;
    left: 50%;
    width: 2px;
    margin-left: -1px;
    background: var(--black-20);
  }
}

.tier-rank-num {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--bg-03);
  color: var(--black-90);
  font: var(--font-med-20);

  &.is-top {
    background: var(--black-90);
    color: var(--white);
  }
}

// shared with the conditions step, so this holds the frame only. anything about the
// define step's own columns belongs on .tier-define-card below.
.tier-card {
  margin: 0 0 16px 16px;
  padding: 16px;
  border: 1px solid var(--black-20);
  border-radius: 8px;
  background: var(--white);

  .tier-card-tag {
    font: var(--font-med-14);
    color: var(--black-50);
    margin-bottom: 12px;
  }
}

// body plus the action column, with room on the left for the absolute drag handle
.tier-define-card {
  display: grid;
  grid-template-columns: 1fr 20px;
  gap: 12px;
  align-items: center;
  padding-left: 40px;
  position: relative;
  overflow: hidden;
}

// keeps the default tier's card aligned with the draggable ones
.tier-drag-spacer {
  display: block;
}

// a 2x3 dot grid, drawn in CSS since the icon set has no drag handle
.tier-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  cursor: grab;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: var(--bg-03);

  svg {
    width: 16px;
    height: 16px;
    color: var(--black-75);
  }
}

.tier-fields {
  display: grid;
  grid-template-columns: 40px minmax(120px, 1.4fr) minmax(140px, 1fr) minmax(140px, 1fr);
  gap: 12px;
  align-items: end;
}

// relative so the clear button can sit on the icon's corner
.tier-icon-wrap {
  position: relative;
  width: 40px;
}

.tier-icon-clear {
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--black-90);
  color: var(--white);
  cursor: pointer;

  svg {
    width: 8px;
    height: 8px;
  }

  &:hover {
    background: var(--alert);
  }
}

.tier-icon-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--black-20);
  border-radius: 8px;
  background: var(--bg-03);
  cursor: pointer;
  overflow: hidden;
  padding: 0;

  svg {
    width: 18px;
    height: 18px;
    color: var(--black-90);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.tier-icon-input {
  display: none;
}

// sits under the name input, which is the second column of the fields grid
.tier-name-error {
  margin: 8px 0 0 52px;
  color: var(--alert);
}

.tier-desc-field {
  margin-top: 12px;
}

.tier-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  height: 100%;
  padding-bottom: 10px;
  position: relative;

  .tier-action {
    display: flex;
    cursor: pointer;
    color: var(--black-75);
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    &.ico-preview {
      top: 116px;
    }

    &.ico-delete {
      top: 148px;
    }

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover,
    &.is-on {
      color: var(--black-90);
    }
  }
}

.tier-preview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--black-20);
}

.tier-preview-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tier-preview-size {
  font: var(--font-fg-12);
  color: var(--black-50);
}

.tier-drop-feedback {
  height: 2px;
  margin: 0 0 16px 48px;
  background: var(--dark-blue);
}
</style>
